import type { KeyboardEvent, SyntheticEvent } from 'react';

type KeyboardAnchorProps = {
  role?: string;
  tabIndex?: number;
  onKeyDown?: (event: KeyboardEvent) => void;
};

/**
 * Returns a11y props for `<a>` elements that render without an `href`
 * (when URL sync is disabled for sandboxed embeds such as Confluence).
 *
 * Without `href`, anchors are dropped from the default tab order and
 * screen readers stop announcing them as interactive. Setting
 * `role="button"`, `tabIndex=0` and a keyboard handler restores both.
 *
 * @param {boolean} isUrlSyncActive - if URL sync is active (href is set on the anchor)
 * @param {(event: SyntheticEvent) => void} activate - handler invoked on Enter / Space
 *
 * @returns {KeyboardAnchorProps} props to spread onto the `<a>` element
 */
export function keyboardAnchorProps(
  isUrlSyncActive: boolean,
  activate: (event: SyntheticEvent) => void,
): KeyboardAnchorProps {
  if (isUrlSyncActive) {
    return {};
  }

  return {
    role: 'button',
    tabIndex: 0,
    onKeyDown: (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        // preventDefault on Space stops page scroll; harmless on Enter
        event.preventDefault();
        activate(event);
      }
    },
  };
}
